/**
 * Persists checkout ecommerce context in sessionStorage so the purchase GA4
 * event can fire reliably on /purchase-complete after billing redirects.
 */

import { trackPurchase } from "@/lib/ga4-client";
import type { GA4EcommerceItem } from "@/types/ga4";

const PENDING_PURCHASE_KEY = "ga4_pending_purchase";
const SENT_PREFIX = "ga4_purchase_sent:";

export type PendingPurchase = {
  items: GA4EcommerceItem[];
  value: number;
  currency: string;
};

export function storePendingPurchase(pending: PendingPurchase): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(PENDING_PURCHASE_KEY, JSON.stringify(pending));
  } catch {
    // ignore quota / private mode
  }
}

export function readPendingPurchase(): PendingPurchase | null {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(PENDING_PURCHASE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;
    const data = parsed as Partial<PendingPurchase>;
    if (
      !Array.isArray(data.items) ||
      typeof data.value !== "number" ||
      typeof data.currency !== "string"
    ) {
      return null;
    }
    return {
      items: data.items,
      value: data.value,
      currency: data.currency,
    };
  } catch {
    return null;
  }
}

export function clearPendingPurchase(): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(PENDING_PURCHASE_KEY);
  } catch {
    // ignore
  }
}

export function getCheckoutTransactionId(): string | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const transactionId =
    params.get("checkout_id")?.trim() || params.get("session_id")?.trim();
  return transactionId || null;
}

function wasPurchaseTracked(transactionId: string): boolean {
  if (typeof sessionStorage === "undefined") return false;
  try {
    return sessionStorage.getItem(`${SENT_PREFIX}${transactionId}`) === "1";
  } catch {
    return false;
  }
}

function markPurchaseTracked(transactionId: string): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(`${SENT_PREFIX}${transactionId}`, "1");
  } catch {
    // ignore
  }
}

/**
 * Fire purchase GA4 from sessionStorage context on /purchase-complete.
 * Idempotent per transaction id (refresh-safe).
 */
/**
 * Adopt Stripe's view of the buyer before the purchase event goes out.
 *
 * Purchase fires from whatever `lba_lead_email` happened to hold, which on a
 * buy-first landing page is often nothing at all — nobody types an email at a
 * pricing card. Stripe, meanwhile, has the name and address they just paid
 * with, confirmed. Writing those into the same stores the tracking already
 * reads means the purchase event picks them up with no signature change, and
 * every later event for this visitor gets them too.
 *
 * Hashing happens server-side, so nothing readable crosses the wire. Purely
 * additive: a failure here leaves the event exactly as thin as it is today.
 */
async function adoptStripeIdentity(sessionId: string): Promise<void> {
  if (!sessionId.startsWith("cs_")) return;
  try {
    const res = await fetch(
      `/api/checkout/session-identity?session_id=${encodeURIComponent(sessionId)}`
    );
    if (!res.ok) return;
    const id = (await res.json()) as {
      sha256_email_address?: string;
      sha256_first_name?: string;
      sha256_last_name?: string;
      country?: string;
    };
    // The stores hold raw values that ga4-client hashes on read, so a
    // pre-hashed email cannot go through rememberLeadEmail. It is already in
    // the right shape for user_data, so it is stashed for the client to use
    // verbatim instead.
    if (id.sha256_email_address || id.sha256_first_name) {
      try {
        window.sessionStorage.setItem(
          "lba_purchase_identity",
          JSON.stringify(id)
        );
      } catch {
        /* private mode — event goes out without these */
      }
    }
  } catch {
    /* network or Stripe hiccup — never block the purchase event */
  }
}

export async function trackPendingPurchaseOnCompletePage(): Promise<void> {
  const transactionId = getCheckoutTransactionId();
  if (!transactionId || wasPurchaseTracked(transactionId)) return;

  const pending = readPendingPurchase();
  if (!pending) return;

  await adoptStripeIdentity(transactionId);

  await trackPurchase(
    transactionId,
    pending.items,
    pending.value,
    pending.currency
  );

  markPurchaseTracked(transactionId);
  clearPendingPurchase();
}
